<template>
    <div id="embed-live" class="embed-live relative w-full aspect-video">
        <video
            ref="video"
            id="amazon-ivs-videojs"
            class="absolute z-10 w-full video-js vjs-skin vjs-16-9 vjs-big-play-centered"
            crossorigin="anonymous"
            :poster="poster"
            :autoplay="true"
            :muted="muted"
            controls
            playsinline
        ></video>
    </div>
</template>

<script>
import { mapState, mapActions } from 'pinia';

export default {
    name: 'EmbedLive',
    props: {
        playbackUrl: {
            required: true,
            type: String,
            default: null,
        },
        playbackSrcDelay: {
            required: false,
            type: Number,
            default: 100,
        },
    },
    data () {
        return {
            player: null,
            error: null,
            poster: this.$config.public.images + 'live-stream-loading.jpg',
            timer: null,
            totalTime: 0,
            lastPlayerTime: null,
        };
    },
    computed: {
        ...mapState(useStoreApp, {
            muted: 'muted',
        }),
    },
    methods: {
        ...mapActions(useStoreApp, {
            muteApp: 'muteApp',
            unmuteApp: 'unmuteApp',
        }),
        async plugins () {
            const videojs = usePluginVideo();
            await videojs.init();
            const ivs = usePluginIvs();
            await ivs.init();
        },
        async init () {
            await this.plugins();
            // https://docs.aws.amazon.com/ivs/latest/LowLatencyUserGuide/player-videojs.html
            this.$registerIVSTech(this.$videojs, {
                wasmWorker: '/assets/ivs/amazon-ivs-wasmworker.min.js',
                wasmBinary: '/assets/ivs/amazon-ivs-wasmworker.min.wasm',
            });
            const element = this.$refs.video;
            const player = this.$videojs(element, {
                techOrder: ['AmazonIVS'],
                bigPlayButton: false,
                controlBar: {
                    pictureInPictureToggle: false,
                },
            });
            setTimeout(() => {
                player.src(this.playbackUrl);
            }, this.playbackSrcDelay);
            player.muted(this.muted);
            player.on('error', this.playerError);
            player.on('ended', this.playerError);
            player.on('playing', this.onPlay);
            player.on('pause', this.onPause);
            player.on('volumechange', this.onVolume);
            let playerEvent = player.getIVSEvents().PlayerEventType;
            player.getIVSPlayer().addEventListener(playerEvent.ERROR, this.ivsError);
            this.player = player;
            this.poster = this.$config.public.images + 'live-stream-loading.jpg';
            if (this.player) this.player.poster(this.poster);
        },
        dispose () {
            if (this.player && this.player.dispose) {
                this.player.dispose();
                this.player = null;
            }
        },
        playerError (error) {
            console.log('PLAYER ERROR', error);
            if (this.timer) clearInterval(this.timer);
            this.handleError();
        },
        ivsError (error) {
            console.log('IVS ERROR', error);
            if (this.timer) clearInterval(this.timer);
            this.handleError();
        },
        handleError () {
            this.poster = this.$config.public.images + 'live-stream-offline.jpg';
            if (this.player) this.player.poster(this.poster);
            setTimeout(() => {
                this.connect();
            }, 10000);
        },
        connect () {
            if (!this.player) return this.init();
            this.player.src(this.playbackUrl);
        },
        toggleFullScreen () {
            const container = document.getElementById('embed-live');
            const fullscreenApi = container.requestFullscreen
                || container.webkitRequestFullScreen
                || container.mozRequestFullScreen
                || container.msRequestFullscreen;
            if (!document.fullscreenElement) {
                fullscreenApi.call(container);
            } else {
                document.exitFullscreen();
            }
            if(this.player) {
                this.player.play();
            }
        },
        onVolume () {
            if (this.player.muted()) {
                this.muteApp();
            } else {
                this.unmuteApp();
            }
        },
        mutePlayer () {
            if (this.player) {
                this.muteApp();
                this.player.muted(this.muted);
            }
        },
        unmutePlayer () {
            if (this.player) {
                this.unmuteApp();
                this.player.muted(this.muted);
            }
        },
        onPlay () {
            this.$emit('play', {totalTime: this.totalTime, currentTime: this.player.currentTime()});
            this.timer = setInterval(() => {
                if (this.lastPlayerTime !== this.player.currentTime()) {
                    this.totalTime += 1;
                }
                this.lastPlayerTime = this.player.currentTime();
                this.$emit('progress', {totalTime: this.totalTime, currentTime: this.player.currentTime()});
            }, 1000);
        },
        onPause () {
            this.$emit('pause', {totalTime: this.totalTime, currentTime: this.player.currentTime()});
            if (this.timer) clearInterval(this.timer);
        },
    },
    mounted () {
        this.init();
    },
    beforeUnmount () {
        if (this.timer) clearInterval(this.timer);
        this.$emit('destroy', {totalTime: this.totalTime, currentTime: this.player?.currentTime(), paused: !!this.player?.paused()});
        this.dispose();
    },
};
</script>
